import React, { useEffect, useState } from 'react';
import {
  Button, Tag, Space, Tooltip, Select
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  DownloadOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, SyncOutlined, CalendarOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { notify } from 'components';
import useUrlFilters from 'hooks/useUrlFilters';

const { Option } = Select;

// Filters/paging live in the URL so the view can be shared as a link.
const URL_FILTERS = {
  status: { type: 'string', default: 'all' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 10 },
};

// `urlPrefix` namespaces the URL params when embedded in a page with its own filters.
const ReportHistoryTable = ({ onRefreshStats, isMobile = false, urlPrefix = '' }) => {
  const [loading, setLoading] = useState(false);
  const [generations, setGenerations] = useState([]);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS, { prefix: urlPrefix });
  const currentPage = filters.page;
  const setCurrentPage = (value) => setFilters({ page: value });
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = (value) => setFilters({ pageSize: value });
  const [totalRows, setTotalRows] = useState(0);
  const statusFilter = filters.status;
  const setStatusFilter = (value) => setFilters({ status: value, page: 1 });

  useEffect(() => {
    fetchGenerations();
  }, [currentPage, pageSize, statusFilter]);

  const fetchGenerations = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await axios.get('/reports/generations/', { params });

      if (response.status === 200) {
        setGenerations(response.data.results || response.data);
        setTotalRows(response.data.count || response.data.length);
      }
    } catch (error) {
      console.error('Error fetching report generations:', error);
      notify('error', 'Failed to load report history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (record) => {
    try {
      // Get export for this generation
      const exportResponse = await axios.get(`/reports/exports/`, {
        params: { generation: record.id }
      });

      if (exportResponse.data.results && exportResponse.data.results.length > 0) {
        const exportData = exportResponse.data.results[0];

        // Download the file
        const downloadResponse = await axios.get(
          `/reports/exports/${exportData.id}/download/`,
          { responseType: 'blob' }
        );

        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', exportData.file_name);
        document.body.appendChild(link);
        link.click();
        link.remove();

        notify('success', 'Report downloaded successfully');
        fetchGenerations(); // Refresh to update download count
      } else {
        notify('warning', 'Report file not found');
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      notify('error', 'Failed to download report');
    }
  };

  const handleRegenerate = async (record) => {
    try {
      const response = await axios.post(`/reports/generations/${record.id}/regenerate/`);

      if (response.status === 201) {
        notify('success', 'Report regeneration started');
        fetchGenerations();
        if (onRefreshStats) onRefreshStats();
      }
    } catch (error) {
      console.error('Error regenerating report:', error);
      notify('error', 'Failed to regenerate report');
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'default', icon: <ClockCircleOutlined /> },
      generating: { color: 'processing', icon: <SyncOutlined spin /> },
      completed: { color: 'success', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', icon: <CloseCircleOutlined /> }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Tag color={config.color} icon={config.icon}>
        {status?.toUpperCase()}
      </Tag>
    );
  };

  const getFormatTag = (format) => {
    const colors = {
      pdf: 'red',
      excel: 'green',
      csv: 'blue',
      json: 'purple'
    };

    return (
      <Tag color={colors[format] || 'default'}>
        {format?.toUpperCase()}
      </Tag>
    );
  };

  // Desktop columns
  const desktopColumns = [
    {
      title: 'Report Name',
      dataIndex: 'report_name',
      key: 'report_name',
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Date Range',
      key: 'date_range',
      width: 180,
      render: (_, record) => (
        <div>
          <div>{dayjs(record.start_date).format('MMM DD, YYYY')}</div>
          <div style={{ fontSize: 12, color: '#888' }}>
            to {dayjs(record.end_date).format('MMM DD, YYYY')}
          </div>
        </div>
      )
    },
    {
      title: 'Format',
      dataIndex: 'export_format',
      key: 'export_format',
      width: 100,
      render: (format) => getFormatTag(format)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Records',
      dataIndex: 'total_records',
      key: 'total_records',
      width: 100,
      align: 'center'
    },
    {
      title: 'Generated By',
      dataIndex: 'generated_by_username',
      key: 'generated_by_username',
      width: 130
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text) => dayjs(text).format('MMM DD, YYYY HH:mm')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.status === 'completed' && (
            <Tooltip title="Download Report">
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
          )}

          <Tooltip title="Regenerate Report">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleRegenerate(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Mobile columns - simplified view
  const mobileColumns = [
    {
      title: 'Report',
      key: 'report_info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{record.report_name}</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
            {getStatusTag(record.status)}
            {getFormatTag(record.export_format)}
          </div>
          <div style={{ fontSize: 11, color: '#888' }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {dayjs(record.start_date).format('MMM DD')} - {dayjs(record.end_date).format('MMM DD, YYYY')}
          </div>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space size="small" direction="vertical">
          {record.status === 'completed' && (
            <Button
              type="primary"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          )}
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => handleRegenerate(record)}
          />
        </Space>
      )
    }
  ];

  const columns = isMobile ? mobileColumns : desktopColumns;

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        gap: isMobile ? 8 : 16,
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: isMobile ? 120 : 150 }}
          size={isMobile ? 'small' : 'middle'}
        >
          <Option value="all">All Status</Option>
          <Option value="pending">Pending</Option>
          <Option value="generating">Generating</Option>
          <Option value="completed">Completed</Option>
          <Option value="failed">Failed</Option>
        </Select>

        <Button
          icon={<ReloadOutlined />}
          onClick={fetchGenerations}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? '' : 'Refresh'}
        </Button>
      </div>

      <Table
        loading={loading}
        dataSource={generations}
        columns={columns}
        rowKey="id"
        size={isMobile ? 'small' : 'middle'}
        scroll={isMobile ? { y: 400 } : { x: 1200, y: 400 }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalRows,
          showSizeChanger: true,
          showQuickJumper: !isMobile,
          showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total} reports`,
          pageSizeOptions: ['10', '25', '50', '100'],
          size: isMobile ? 'small' : 'default',
          onChange: (page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) {
              setPageSize(size);
              setCurrentPage(1);
            }
          }
        }}
      />
    </div>
  );
};

export default ReportHistoryTable;
